/**
 * Default wizard values and the correlation id generator.
 *
 * Every default here that is not dictated by the contract is a TODO-confirm
 * placeholder (see config/workwise.ts). Server sizes are T-shirt tiers whose
 * VM sizes come from the admin config of the `workwise` product; a dedicated DB
 * and gateway use the step-2 size of the app server (R3-G).
 *
 * Node-safe: relative imports only, no import.meta.
 */
import {
  DEFAULT_APP_DATA_DISK_GB,
  DEFAULT_DB_STORAGE,
  DEFAULT_DISK_TYPE,
  DEFAULT_NSG_RULES,
  NON_PRODUCTION_DISK_TYPE,
  DEFAULT_OS,
  DEFAULT_SKIP_APPLICATION,
  DEFAULT_SMTP_PORT,
  DEFAULT_SQL_AUTHENTICATION,
  DEFAULT_STORAGE_WORKLOAD,
} from '../../config/workwise'
import type {
  DbStorageFormValue,
  DiskType,
  WorkwiseCicd,
  WorkwiseEnvironment,
  WorkwiseFormValues,
  WorkwiseSku,
} from '../../types/workwise'

export interface DefaultsOptions {
  /** Pre-fills tags.owner (e.g. the signed-in user's e-mail). */
  owner?: string
  /** Pre-fills app.version (first entry of GET /workwise/versions/). */
  version?: string
  /** Values owned by the standard wizard steps (environment, region, CI/CD, step-2 size). */
  environment?: WorkwiseEnvironment
  region?: string
  cicd?: WorkwiseCicd
  appSku?: WorkwiseSku
  /** Starts with the Application (ERP) step skipped (default: DEFAULT_SKIP_APPLICATION). */
  skipApplication?: boolean
}

/**
 * The disk type a new disk starts with: Standard SSD in an environment other than production (the RnD subscriptions
 * deny Premium disks), the contract default (Premium SSD) for production and when no environment is known.
 */
export function defaultDiskType(environment?: WorkwiseEnvironment): DiskType {
  return environment !== undefined && environment !== 'production' ? NON_PRODUCTION_DISK_TYPE : DEFAULT_DISK_TYPE
}

/** A fresh copy of the default "SQL Server storage": data 512 GB x 1, log 256 GB x 1, TempDB on the local SSD. */
export function defaultDbStorage(diskType: DiskType = DEFAULT_DISK_TYPE): DbStorageFormValue {
  return {
    data: { ...DEFAULT_DB_STORAGE.data, type: diskType },
    log: { ...DEFAULT_DB_STORAGE.log, type: diskType },
    tempdb: { ...DEFAULT_DB_STORAGE.tempdb, type: diskType },
  }
}

export function defaultFormValues(options: DefaultsOptions = {}): WorkwiseFormValues {
  const environment = options.environment ?? 'stg'
  const region = options.region ?? 'East US 2'
  const sku = options.appSku ?? 'M'
  const diskType = defaultDiskType(options.environment)
  return {
    customer: { name: '', number: '', slug: '', confirmNumber: '' },
    environment,
    region,
    cicd: options.cicd ?? 'github_actions',
    // TODO-confirm: SaaS-Hybrid (shared DB + gateway, one VM) as the default model.
    deploymentModel: 'saas-hybrid',
    appServer: {
      sku,
      vmName: '',
      os: DEFAULT_OS,
      osDiskType: diskType,
      disks: [{ sizeGb: DEFAULT_APP_DATA_DISK_GB, type: diskType }],
      zone: '',
    },
    dbServer: {
      sharedServerId: undefined,
      vmName: '',
      sqlAuthentication: DEFAULT_SQL_AUTHENTICATION,
      sqlLogin: '',
      storageWorkload: DEFAULT_STORAGE_WORKLOAD,
      storage: defaultDbStorage(diskType),
      zone: '',
    },
    gatewayServer: {
      sharedServerId: undefined,
      vmName: '',
      publicIp: true,
      disks: [],
      nsgRules: DEFAULT_NSG_RULES.map((rule) => ({ ...rule })),
      zone: '',
    },
    // Revision 4: no default ranges (only placeholders: they must not overlap another WorkWise
    // network); SaaS-Hybrid starts on the shared database server's network.
    network: { vnetCidr: '', subnetCidr: '', existingNetworkId: undefined },
    skipApplication: options.skipApplication ?? DEFAULT_SKIP_APPLICATION,
    app: {
      version: options.version ?? '',
      tenantName: '',
      licenseKeyRef: '',
      smtp: {
        host: '',
        port: DEFAULT_SMTP_PORT,
        username: '',
        passwordRef: '',
        fromAddress: '',
        useTls: true,
      },
      dbConnectionRef: '',
    },
    tags: {
      owner: options.owner ?? '',
      extra: [],
    },
  }
}

// ─── Correlation id ──────────────────────────────────────────────────────────

type RandomSource = {
  randomUUID?: () => string
  getRandomValues?: (array: Uint8Array) => unknown
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * A real RFC 4122 version 4 UUID. Uses crypto.randomUUID when present, falls
 * back to crypto.getRandomValues (plain-http origins have no randomUUID), and
 * only as a last resort to Math.random. The version and variant bits are set in
 * both fallbacks, so the backend's v4 check always passes.
 */
export function generateCorrelationId(source: RandomSource | undefined = globalThis.crypto): string {
  if (source && typeof source.randomUUID === 'function') {
    return source.randomUUID()
  }
  const bytes = new Uint8Array(16)
  if (source && typeof source.getRandomValues === 'function') {
    source.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = toHex(bytes)
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
